import React from "react";
import type { BaseEntity, SimpleTableProps } from "../types";
import type { SimpleTableSetupResult } from "../SimpleTableSetup";
import type { SimpleTableStateSetupResult } from "../SimpleTableStateSetup";
import type { SimpleTableCallbackWrappersResult } from "../SimpleTableCallbacksSetup";
import type {
  SimpleTableContextValue,
  SimpleTableStableValue,
  SimpleTableVolatileValue,
  SimpleTableChromeValue,
} from "../contexts/SimpleTableContext";
import { buildStateSlice } from "./state-slice";
import { buildStableSlice, adaptInlineEdit } from "./stable-slice";
import { buildChromeSlice } from "./chrome-slice";
import { buildCallbacksSlice, type ComposedTableCallbacks } from "./callbacks-slice";

/**
 * The RENDER-TIME entry point for the SimpleTable context value.
 *
 * `createSimpleTableContextValue` is a plain function: it allocates a fresh
 * top-level object AND a fresh nested `state` object on every call, and it was
 * called unconditionally from `SimpleTable`'s render. A context change bypasses
 * `React.memo`, so `MemoizedTableRow` / `MemoizedTableCell` never short-circuited
 * and every `SimpleTable` render repainted ~35 virtualized rows x N cells.
 *
 * This hook returns the three slices, each memoized on its OWN members. The
 * dependency lists are deliberately member-level: `setup`, `stateSetup` and
 * `callbackWrappers` are themselves freshly-built objects every render, so a
 * `useMemo` keyed on them would be a no-op — the exact bug this file exists to
 * remove.
 */
export interface SimpleTableContextSlices<TData extends BaseEntity> {
  stable: SimpleTableStableValue<TData>;
  volatile: SimpleTableVolatileValue<TData>;
  chrome: SimpleTableChromeValue<TData>;
}

type ValueProps<TData extends BaseEntity> = Partial<SimpleTableProps<TData>> &
  Pick<SimpleTableProps<TData>, "hooks">;

export function useSimpleTableContextValue<TData extends BaseEntity>(
  setup: SimpleTableSetupResult<TData>,
  stateSetup: SimpleTableStateSetupResult,
  callbackWrappers: SimpleTableCallbackWrappersResult,
  callbacks: ComposedTableCallbacks<TData>,
  props: ValueProps<TData>
): SimpleTableContextSlices<TData> {
  const {
    handleSelectItem,
    handleSelectAll,
    handleCellEditCallback,
    handleCellSaveCallback,
    handleCellCancelCallback,
    handleSort,
    removeSortColumn,
    clearAllSorting,
    handleCustomRangeClick,
    handleCustomRangeApply,
    handleCustomRangeClose,
    resetColumnWidthsCallback,
    handleColumnResizeCallback,
    handleDragStartCallback,
    handleDragOverCallback,
    handleDropCallback,
    handleDragEndCallback,
    handleBulkEditField,
    handleBulkEditAllFiltered,
    handleBulkAction,
    handleBodyScroll,
    handleHeaderScroll,
    addAdvancedFilter,
    removeAdvancedFilter,
    clearAllAdvancedFilters,
  } = callbacks;

  const callbacksSlice = React.useMemo(
    () => buildCallbacksSlice<TData>(callbacks),
    // Member-level on purpose: `callbacks` is a fresh object literal each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handleSelectItem,
      handleSelectAll,
      handleCellEditCallback,
      handleCellSaveCallback,
      handleCellCancelCallback,
      handleSort,
      removeSortColumn,
      clearAllSorting,
      handleCustomRangeClick,
      handleCustomRangeApply,
      handleCustomRangeClose,
      resetColumnWidthsCallback,
      handleColumnResizeCallback,
      handleDragStartCallback,
      handleDragOverCallback,
      handleDropCallback,
      handleDragEndCallback,
      handleBulkEditField,
      handleBulkEditAllFiltered,
      handleBulkAction,
      handleBodyScroll,
      handleHeaderScroll,
      addAdvancedFilter,
      removeAdvancedFilter,
      clearAllAdvancedFilters,
    ]
  );

  const onInlineEdit = React.useMemo(
    () => adaptInlineEdit<TData>(props.onInlineEdit),
    [props.onInlineEdit]
  );

  const stable = React.useMemo(
    () => buildStableSlice<TData>(setup, callbacksSlice, props, onInlineEdit),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setup.config,
      setup.visibleFields,
      setup.data,
      setup.appUser,
      setup.roleName,
      setup.containerRef,
      setup.tableHeaderScrollRef,
      setup.tableBodyRef,
      callbacksSlice,
      props.hooks,
      props.onEdit,
      props.onView,
      props.onDelete,
      onInlineEdit,
      props.onOpenDetailModal,
    ]
  );

  const stateSlice = React.useMemo(
    () =>
      buildStateSlice<TData>(
        setup,
        stateSetup,
        callbackWrappers
      ) as unknown as SimpleTableContextValue<TData>["state"],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setup.filterContext.filters,
      setup.filterContext.uiState,
      setup.filterContext.actions,
      setup.quickFilterState,
      setup.setQuickFilterState,
      stateSetup.sortField,
      stateSetup.sortDirection,
      stateSetup.sortColumns,
      stateSetup.selectedItems,
      stateSetup.showFloatingBulkActions,
      stateSetup.columnWidths,
      stateSetup.columnOrder,
      stateSetup.draggedColumn,
      stateSetup.editingCell,
      stateSetup.editValue,
      stateSetup.effectiveCurrentPage,
      stateSetup.effectiveSetCurrentPage,
      stateSetup.effectivePageSize,
      stateSetup.effectiveSetPageSize,
      callbackWrappers.wrappedSetSortColumns,
      callbackWrappers.wrappedSetSortField,
      callbackWrappers.wrappedSetSortDirection,
      callbackWrappers.wrappedSetSelectedItems,
      callbackWrappers.wrappedSetColumnWidths,
      callbackWrappers.wrappedSetEditingCell,
      callbackWrappers.wrappedSetEditValue,
      callbackWrappers.wrappedSetDraggedColumn,
      callbackWrappers.wrappedSetShowFloatingBulkActions,
      callbackWrappers.wrappedSetColumnOrder,
    ]
  );

  const volatile = React.useMemo<SimpleTableVolatileValue<TData>>(
    () => ({
      state: stateSlice,
      utils: { getColumnStyleCallback: stateSetup.getColumnStyleCallback },
    }),
    [stateSlice, stateSetup.getColumnStyleCallback]
  );

  const chrome = React.useMemo(
    () => buildChromeSlice<TData>(setup, props),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setup.processedData,
      setup.paginatedData,
      setup.totalPages,
      setup.openModal,
      setup.config.savedViews,
      props.groupingContext,
      props.groupableFields,
      props.onCreate,
      props.onBulkAction,
      props.savedViewsControls,
      props.loadMore,
      props.hasNextPage,
      props.isFetchingNextPage,
      props.autoScrollEnabled,
      props.totalItems,
      props.totalPages,
      props.onPageChange,
      props.error,
      props.isLoading,
      props.hideColumnHeaders,
      props.hideTableBody,
    ]
  );

  return { stable, volatile, chrome };
}
